import React, { useState, useEffect, useRef } from 'react'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import { t } from 'ttag'
import { makeStyles } from '@material-ui/core/styles'
import { ReactComponent as FindIcon } from '../../assets/v3/icons/ranking/find.svg'
import { ReactComponent as ClearIcon } from '../../assets/v3/icons/clear.svg'
import { isRtl } from '../../i18nInit'

// Define styles using makeStyles
const useStyles = makeStyles(() => ({
  adornedStart: {
    // Apply padding on the correct side based on text direction
    ...(isRtl ? { paddingRight: '9px !important' } : { paddingLeft: '9px !important' }),
    '& svg': {
      position: 'relative',
      top: '0.5px',
    },
  },
  adornedEnd: {
    ...(!isRtl ? { paddingRight: '4px !important' } : { paddingLeft: '4px !important' }),
  },
  input: {
    paddingTop: '8px',
    paddingBottom: '8px',
    fontSize: '14px',
    lineHeight: '20px',
    height: '20px',
  },
  inputNarrow: {
    paddingTop: '6px',
    paddingBottom: '6px',
    fontSize: '14px',
    lineHeight: '20px',
    height: '20px',
  },
  notchedOutline: {
    borderColor: 'white',
    borderRadius: '8px',
  },
  rootWhite: {
    backgroundColor: 'white !important',
    borderRadius: '8px',
  },
}))

interface YumSearchFieldProps {
  /**
   * The current search value
   */
  value: string
  /**
   * Callback function when search value changes
   */
  onChange: (value: string) => void
  /**
   * Placeholder text for the search field
   * @default "Search"
   */
  placeholder?: string
  /**
   * Whether to automatically focus the search field when mounted
   * @default true
   */
  autoFocus?: boolean
  /**
   * Additional CSS styles for the search field
   */
  style?: React.CSSProperties
  /**
   * Debounce time in milliseconds before triggering onChange
   * @default 400
   */
  debounceTime?: number
  /**
   * Optional callback function when clear button is clicked
   */
  onClear?: () => void
  /**
   * Whether to always show the clear button, even when the field is empty
   * @default false
   */
  alwaysShowClearButton?: boolean
  isChallengeView?: boolean
  /**
   * Whether to apply white background styling
   * @default false
   */
  whiteBackground?: boolean
}

/**
 * A reusable search field component with clear button and search icon
 */
const YumSearchField: React.FC<YumSearchFieldProps> = ({
  value,
  onChange,
  placeholder = t`Search`,
  autoFocus = true,
  style,
  debounceTime = 400,
  onClear,
  alwaysShowClearButton = false,
  isChallengeView = false,
  whiteBackground = false,
}) => {
  // Get styles from makeStyles
  const classes = useStyles()

  // Create a ref for the input element
  const inputRef = useRef<HTMLInputElement>(null)

  // Internal state for the input value
  const [searchValue, setSearchValue] = useState<string>(value)

  // Update internal state when prop value changes
  useEffect(() => {
    setSearchValue(value)
  }, [value])

  // Debounce the search value changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== value) {
        onChange(searchValue)
        //console.log('debounced searchValue', searchValue)
      }
    }, debounceTime)

    return () => clearTimeout(timer)
  }, [searchValue, onChange, debounceTime, value])

  // Debounce the search value changes
  //   useEffect(() => {
  //     onChange(searchValue)
  //   }, [searchValue])

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  // Handle clear button click
  const handleClear = () => {
    setSearchValue('')
    // Call the onClear callback if provided
    if (onClear) {
      onClear()
    }
  }

  // Handle key down events (for dismissing mobile keyboard)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Use the ref to blur the input
      if (inputRef.current) {
        inputRef.current.blur()
      }
    }
  }

  return (
    <TextField
      value={searchValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      variant="outlined"
      size="small"
      fullWidth
      autoFocus={autoFocus}
      style={style}
      inputRef={inputRef}
      InputProps={{
        className: whiteBackground ? classes.rootWhite : '',
        startAdornment: (
          <InputAdornment position="start">
            <FindIcon />
          </InputAdornment>
        ),
        endAdornment:
          searchValue || alwaysShowClearButton ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} style={{ padding: '6px' }}>
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ) : null,
        classes: {
          adornedStart: classes.adornedStart,
          adornedEnd: classes.adornedEnd,
          input: isChallengeView ? classes.inputNarrow : classes.input,
          notchedOutline: isChallengeView || whiteBackground ? classes.notchedOutline : '',
        },
      }}
    />
  )
}

export default YumSearchField
